import '.ir{background-color:var(--Shades-Yellow-200, #FFF0CA);}.ssr-plugin'
import '.cq{border-radius:0.75rem;}.ssr-plugin'
import '.is{padding:0.875rem 1rem;}.ssr-plugin'
import '.d{display:flex;}.ssr-plugin'
import '.ao{justify-content:space-between;}.ssr-plugin'
import '.g{gap:1rem;}.ssr-plugin'
import '.ae{flex-shrink:0;}.ssr-plugin'
import '.bd{justify-content:flex-end;}.ssr-plugin'
import '.e2{column-gap:1rem;}.ssr-plugin'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { useTranslations } from '@i18n/useLanguage'
import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'wouter'
import { useClient, useQuery, useAuthState } from '@based/sdk/react'
import { styled } from 'inlines'
import { Button } from '@app/components/core/button/Button'
import { Text } from '@app/components/core/typography/Text'
import { Modal } from '@app/components/blocks/modal/Modal'
import { DiscussionCard } from '@app/components/blocks/discussions/DiscussionCard'
import { PostDetailModal } from '@app/components/blocks/discussions/PostDetailModal'
import { CreatePostDrawer } from '@app/components/blocks/discussions/CreatePostDrawer'
import { fullName } from '@app/util/helpers'
import { useAuthModal } from '@app/util/hooks/useAuthModal'
import { usePostActions } from '@app/util/hooks/usePostActions'
import { getPostShareUrl, getPostShareTitle } from '@app/pages/discussions/discussionsHelpers'
import { PostViewModel } from 'functions/app/thread/view'
import { PostModel, ThreadModel } from '@app/types/Thread'
import { GlobalPostViewModel } from 'functions/app/thread/view-global'
import { Empty } from '@app/components/blocks/empty/Empty'
import SearchForGroupImage from '@app/assets/images/search-for-group.png'
import { faPlus, faSparkles } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRelativeDateFormat } from '@app/util/useDateFormat'
import useMediaQuery from '@app/util/useMediaQuery'
import {
  AppearSection,
  AppearItem,
} from '../contentSection/blocks/AppearInViewport'

const mapPost = (post: PostViewModel): PostModel => {
  return {
    id: post.id,
    authorId: post.author?.id,
    authorName: post.author
      ? fullName({
          firstName: post.author.firstName,
          lastName: post.author.lastName,
          middlePart: post.author.middlePart,
        })
      : '',
    authorAvatar: post.author?.avatar?.src,
    title: post.title ?? undefined,
    creationDate: new Date(post.createdAt),
    updatedAt: post.updatedAt ? new Date(post.updatedAt) : undefined,
    content: post.content,
    media: post.media
      .filter((m) => m.status === 'ready')
      .map((m) => ({ id: m.id, src: m.src, mimeType: m.mimeType, name: m.name })),
    categories: post.categories?.map((c) => ({
      id: c.id,
      name: c.name,
      primaryColour: c.primaryColour,
      backgroundColour: c.backgroundColour,
      borderColour: c.borderColour,
    })),
    threads: post.threads,
    reactionUserIds: post.viewers
      .filter((v: { $reaction: string }) => v.$reaction === 'like')
      .map((v: { id: any }) => v.id.toString()),
    comments: post.replies ? post.replies.map(mapPost) : [],
    hasMoreComments: false,
  }
}

export const Thread = ({
  threadId,
  // kept for backwards compatibility — Thread now manages its own edit drawer
  onEditPost: _onEditPost,
  columns = 2,
}: {
  threadId: number
  onEditPost?: (model: GlobalPostViewModel) => void
  columns?: 2 | 3
}) => {
  const t = useTranslations().app.plotPage.threadTab
  const tDiscussionsRoot = useTranslations().app.discussions
  const client = useClient()
  const { withAuthCheck } = useAuthModal()
  const authState = useAuthState()
  const { deletePost } = usePostActions()

  // --- drawer state ---
  const [isCreateDrawerOpen, setIsCreateDrawerOpen] = useState(false)
  const [editPost, setEditPost] = useState<GlobalPostViewModel | null>(null)

  // --- post action state ---
  const [selectedPost, setSelectedPost] = useState<PostModel | null>(null)
  const [previewPostId, setPreviewPostId] = useState<number | null>(null)
  const [confirmDeletePostId, setConfirmDeletePostId] = useState<number | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const relativeDate = useRelativeDateFormat()
  const isMobile = useMediaQuery()

  const [mounted, setMounted] = useState(false)
  const [clientThread, setClientThread] = useState<ThreadModel | null>(null)

  const threadQuery = useQuery('view-thread', { id: threadId })
  const serverThread: ThreadModel | null = useMemo(() => {
    if (!threadQuery.data) {
      return null
    }
    return {
      id: threadQuery.data.id,
      posts: threadQuery.data.posts ? threadQuery.data.posts.map(mapPost) : [],
    }
  }, [threadQuery.data])

  const thread = mounted ? clientThread : serverThread

  useEffect(() => {
    if (!threadId) {
      return
    }
    const isAuthed = !!(authState && authState.userId)
    let scrolled = false
    const unsubscribe = client.query('view-thread', { id: threadId }).subscribe((result) => {
      setClientThread({
        id: result.id,
        posts: result.posts ? result.posts.map(mapPost) : [],
      })
      setMounted(true)

      // Scroll to the anchored post once posts have rendered.
      // Use requestAnimationFrame so the DOM has been painted first.
      if (!scrolled && window.location.hash.startsWith('#post-')) {
        scrolled = true
        requestAnimationFrame(() => {
          const el = document.querySelector(window.location.hash)
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' })
          }
        })
      }
    })
    if (isAuthed) {
      client.call('read-thread', { threadId }).catch(() => {})
    }
    return () => unsubscribe()
  }, [client, threadId, authState])

  // Open the post modal when arriving via a ?post=123 link (e.g. from the
  // overview page thread section). Prefer the full PostModel when it's already
  // in the loaded thread, otherwise fall back to previewPostId so the modal
  // self-fetches via view-post.
  useEffect(() => {
    const postParam = searchParams.get('post')
    if (!postParam) {
      return
    }
    const targetId = Number(postParam)
    if (!targetId) {
      return
    }
    const found = thread?.posts?.find((post) => post.id === targetId)
    if (found) {
      setSelectedPost(found)
      setPreviewPostId(null)
    } else {
      setPreviewPostId(targetId)
    }
  }, [searchParams, thread])

  const handleModalClose = () => {
    setSelectedPost(null)
    setPreviewPostId(null)
    if (searchParams.has('post')) {
      const next = new URLSearchParams(searchParams)
      next.delete('post')
      setSearchParams(next)
    }
  }

  const react = (postId: number) => {
    void withAuthCheck(async () => {
      await client.call('react-post', { postId })
    })
  }

  const handleEdit = (post: PostModel) => {
    // Construct a GlobalPostViewModel for CreatePostDrawer.
    // post.threads carries the full entity context fetched by view-thread,
    // so the drawer can pre-populate location and destination selectors.
    const editModel: GlobalPostViewModel = {
      id: post.id,
      title: post.title ?? undefined,
      content: post.content,
      createdAt: post.creationDate.getTime(),
      updatedAt: undefined,
      visibility: undefined,
      media: post.media.map((m) => ({
        id: m.id,
        src: m.src,
        status: 'ready',
        mimeType: m.mimeType,
        name: m.name,
      })),
      categories: post.categories ?? [],
      threads: post.threads ?? [],
      author: undefined,
      viewers: [],
      replies: [],
    }
    setEditPost(editModel)
  }

  const handleDelete = async () => {
    if (confirmDeletePostId === null) return
    setDeleteLoading(true)
    const ok = await deletePost(confirmDeletePostId)
    if (ok) {
      setClientThread((prev) =>
        prev
          ? { ...prev, posts: prev.posts.filter((p) => p.id !== confirmDeletePostId) }
          : prev,
      )
      setConfirmDeletePostId(null)
    }
    setDeleteLoading(false)
  }

  const allPosts = useMemo(() => thread?.posts ?? [], [thread?.posts])

  return (
    <Column gap="1.5rem" alignItems="stretch">
      {/* Alert banner with inline post button */}
      <div className="ir cq is d ao g"
        style={{
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'flex-start' : 'center',}}
      >
        <Column gap="0.25rem" style={{ flex: 1 }}>
          <Row gap="0.375rem" style={{ alignItems: 'center' }}>
            <Text.Medium isBold={true} style={{ color: 'var(--brand-secondary)' }}>
              {t.alertTitle}
            </Text.Medium>
            <FontAwesomeIcon
              icon={faSparkles}
              style={{
                width: '1rem',
                height: '1rem',
                color: 'var(--palette-yellow-400, #FACC15)',
              }}
            />
          </Row>
          <Text.Small style={{ color: 'var(--brand-secondary)', opacity: 0.8 }}>
            {t.alertDescription}
          </Text.Small>
        </Column>
        <div className="ae d bd"
          style={{
            alignSelf: isMobile ? 'stretch' : 'auto',
          }}
        >
          <Button
            variant="primary"
            label={t.newPost}
            icon={faPlus}
            iconPosition="left"
            onClick={() => setIsCreateDrawerOpen(true)}
          />
        </div>
      </div>

      {/* Posts list */}
      {allPosts.length > 0 ? (
        <AppearSection>
          <div className="e2"
            style={{
              columnCount: isMobile ? 1 : columns,}}
          >
          {allPosts.map((post, index) => {
            const liked =
              !!authState.userId &&
              post.reactionUserIds.includes(authState.userId.toString())
            const isPostAuthor =
              !!authState.userId &&
              post.authorId === Number(authState.userId)
            return (
              <AppearItem
                key={post.id}
                staggerIndex={index % (isMobile ? 1 : columns)}
                style={{ breakInside: 'avoid', marginBottom: '1rem' }}
              >
                <DiscussionCard
                  authorName={post.authorName}
                  authorAvatar={post.authorAvatar}
                  date={relativeDate(post.creationDate)}
                  title={post.title ?? ''}
                  updatedAt={post.updatedAt ? relativeDate(post.updatedAt) : undefined}
                  content={post.content ?? ''}
                  likes={post.reactionUserIds.length}
                  liked={liked}
                  onLikeClick={() => react(post.id)}
                  replies={post.comments.reduce(
                    (total, c) => total + 1 + c.comments.length,
                    0,
                  )}
                  categories={post.categories}
                  media={post.media}
                  onClick={() => setSelectedPost(post)}
                  onEdit={isPostAuthor ? () => handleEdit(post) : undefined}
                  onDelete={
                    isPostAuthor
                      ? () => setConfirmDeletePostId(post.id)
                      : undefined
                  }
                  shareUrl={getPostShareUrl(post.id)}
                  shareTitle={getPostShareTitle(post.title ?? undefined, post.content ?? undefined)}
                />
              </AppearItem>
            )
          })}
          </div>
        </AppearSection>
      ) : allPosts.length === 0 ? (
        <Empty
          title={t.noPostsTitle}
          style={{ marginTop: '4rem' }}
          imageSrc={SearchForGroupImage}
        >
          {t.noPostsDescription}
        </Empty>
      ) : null}

      {/* Post detail modal */}
      <PostDetailModal
        postId={selectedPost?.id ?? previewPostId}
        threadId={threadId}
        authorName={selectedPost?.authorName ?? ''}
        date={selectedPost ? relativeDate(selectedPost.creationDate) : ''}
        title={selectedPost?.title ?? ''}
        updatedAt={selectedPost?.updatedAt ? relativeDate(selectedPost.updatedAt) : undefined}
        categories={selectedPost?.categories}
        onClose={handleModalClose}
        onEdit={
          selectedPost?.authorId === Number(authState.userId)
            ? () => {
                handleEdit(selectedPost)
                setSelectedPost(null)
              }
            : undefined
        }
        onDelete={
          selectedPost?.authorId === Number(authState.userId)
            ? () => {
                setConfirmDeletePostId(selectedPost.id)
                setSelectedPost(null)
              }
            : undefined
        }
      />

      {/* Create post drawer */}
      <CreatePostDrawer
        open={isCreateDrawerOpen}
        threadId={threadId}
        context="thread"
        onClose={() => setIsCreateDrawerOpen(false)}
      />

      {/* Edit post drawer */}
      <CreatePostDrawer
        open={!!editPost}
        editPost={editPost}
        context="thread"
        onClose={() => setEditPost(null)}
      />

      {/* Delete confirmation */}
      <Modal
        isOpen={confirmDeletePostId !== null}
        loading={deleteLoading}
        backLabel={tDiscussionsRoot.cancel}
        confirmLabel={tDiscussionsRoot.deleteConfirm}
        onClose={() => setConfirmDeletePostId(null)}
        onConfirm={() => void handleDelete()}
      >
        <Text.Medium>{tDiscussionsRoot.deleteConfirmDescription}</Text.Medium>
      </Modal>
    </Column>
  )
}
